import './ui/styles.css';
import { loadState, StateManager } from './engine/state';
import { TickLoop } from './engine/tick';
import { CommandParser } from './parser/command';
import { Renderer } from './ui/renderer';
import { InputHandler } from './ui/input';
import { resolveFish, getFishXp } from './game/fishing';
import { getLocationDefById } from './game/locations';
import { calcBonus } from './game/skills';
import { SELL_XP } from './game/constants';

import { getVesselCap } from './game/vessels';

const outputEl = document.getElementById('output') as HTMLElement;
const inputEl = document.getElementById('cmd-input') as HTMLInputElement;
const statusBarEl = document.getElementById('status-bar') as HTMLElement;
const fishingAnimEl = document.getElementById('fishing-anim') as HTMLElement;
const hintEl = document.getElementById('autocomplete-hint') as HTMLElement;

const { state: initialState, offlineLog } = loadState();
const stateManager = new StateManager(initialState);
const renderer = new Renderer(outputEl, statusBarEl, fishingAnimEl);
const parser = new CommandParser(stateManager);
const tickLoop = new TickLoop(stateManager);

let fishingTimeout: ReturnType<typeof setTimeout> | null = null;
let fishingAnimFrame = 0;
let fishingAnimInterval: ReturnType<typeof setInterval> | null = null;

let isTraveling = false;
let transitTimeout: ReturnType<typeof setTimeout> | null = null;
let transitAnimFrame = 0;
let transitAnimInterval: ReturnType<typeof setInterval> | null = null;

function updateStatusBar(): void {
  const state = stateManager.getState();
  const location = getLocationDefById(state.currentLocation);
  const totalVessels = state.vessels.length;
  const dockProp = state.properties.find(p => p.id === 'dock');
  const dockTier = dockProp?.tier ?? 0;
  const businessLevel = state.playerStats.skills.business.level;
  const cap = getVesselCap(businessLevel, dockTier);
  renderer.updateStatusBar(
    state.coins,
    location?.name || state.currentLocation,
    state.inventory.length,
    state.isFishing,
    state.playerStats.level,
    totalVessels,
    cap,
  );
}

function startFishingAnimation(): void {
  renderer.showFishingAnimation(true);
  fishingAnimFrame = 0;
  if (fishingAnimInterval) clearInterval(fishingAnimInterval);
  fishingAnimInterval = setInterval(() => {
    fishingAnimFrame++;
    renderer.updateFishingAnimation(fishingAnimFrame);
  }, 400);
}

function stopFishingAnimation(): void {
  renderer.showFishingAnimation(false);
  if (fishingAnimInterval) {
    clearInterval(fishingAnimInterval);
    fishingAnimInterval = null;
  }
}

function startTransitAnimation(destination: string): void {
  isTraveling = true;
  renderer.showTransitAnimation(destination);
  transitAnimFrame = 0;
  if (transitAnimInterval) clearInterval(transitAnimInterval);
  transitAnimInterval = setInterval(() => {
    transitAnimFrame++;
    renderer.updateTransitAnimation(transitAnimFrame);
  }, 250);
  transitTimeout = setTimeout(() => {
    stopTransitAnimation();
    isTraveling = false;
    updateStatusBar();
  }, 1000);
}

function stopTransitAnimation(): void {
  renderer.hideTransitAnimation();
  if (transitAnimInterval) {
    clearInterval(transitAnimInterval);
    transitAnimInterval = null;
  }
  if (transitTimeout) {
    clearTimeout(transitTimeout);
    transitTimeout = null;
  }
}

function scheduleFishResolution(): void {
  if (fishingTimeout) clearTimeout(fishingTimeout);
  const state = stateManager.getState();
  const generation = state.fishingGeneration;
  const location = getLocationDefById(state.currentLocation);
  const tier = location?.fishTier ?? 0;

  const fishingSkill = state.playerStats.skills.fishing.level;
  const rodLevel = state.equipment.find(e => e.id === 'rod')?.level ?? 1;
  const speedMult = calcBonus(fishingSkill, 0.042) * calcBonus(rodLevel, 0.07);

  const baseDelay = (3000 + tier * 500) / speedMult;
  const delay = baseDelay + (Math.random() * (3000 + tier * 1000)) / speedMult;
  fishingTimeout = setTimeout(() => {
    fishingTimeout = null;
    const currentState = stateManager.getState();
    if (!currentState.isFishing || currentState.fishingGeneration !== generation) return;
    const result = resolveFish(currentState);
    if (Object.keys(result.statePatch).length > 0) {
      stateManager.dispatch(result.statePatch, result.logEntries);

      const xpEntries = stateManager.addPlayerXp(getFishXp(tier));
      if (xpEntries.length > 0) {
        renderer.append(xpEntries);
        updateStatusBar();
      }
    }
    stopFishingAnimation();
  }, delay);
}

function handleSellXp(): void {
  const xpEntries = stateManager.addPlayerXp(SELL_XP);
  if (xpEntries.length > 0) {
    renderer.append(xpEntries);
    updateStatusBar();
  }
}

if (offlineLog) {
  renderer.append([offlineLog]);
}

renderer.append([{
  text: '⚓ Welcome to the Idle Fishing Game!',
  type: 'system',
  timestamp: Date.now(),
}, {
  text: 'Type >help for commands or >tutorial for a quick start guide.',
  type: 'info',
  timestamp: Date.now(),
}]);

stateManager.subscribe((state, logEntries) => {
  if (logEntries.length > 0) {
    renderer.append(logEntries);
  }
  updateStatusBar();

  if (state.isFishing && !fishingTimeout) {
    startFishingAnimation();
    scheduleFishResolution();
  }
});

new InputHandler(inputEl, (input: string) => {
  const state = stateManager.getState();

  if (isTraveling) {
    renderer.append([{
      text: 'You are traveling...',
      type: 'error',
      timestamp: Date.now(),
    }]);
    return;
  }

  if (state.isFishing && input.trim().toLowerCase() !== 'cancel') {
    renderer.append([{
      text: 'You are fishing! Type >cancel to reel in early.',
      type: 'error',
      timestamp: Date.now(),
    }]);
    return;
  }

  const result = parser.parse(input);
  if (result.clear) {
    renderer.clear();
  }
  if (result.logEntries.length > 0) {
    renderer.append(result.logEntries);
  }

  if (result.transitTo) {
    const destinationDef = getLocationDefById(result.transitTo);
    startTransitAnimation(destinationDef?.name || result.transitTo);
    return;
  }

  updateStatusBar();

  const trimmed = input.trim().toLowerCase();
  if (trimmed.startsWith('sell') && result.success) {
    handleSellXp();
  }

  const newState = stateManager.getState();
  if (newState.isFishing && !fishingTimeout) {
    startFishingAnimation();
    scheduleFishResolution();
  }
}, hintEl, () => stateManager.getState(), parser);

tickLoop.start();
updateStatusBar();

window.addEventListener('beforeunload', () => {
  stateManager.forceSave();
  tickLoop.stop();
  if (fishingTimeout) clearTimeout(fishingTimeout);
  if (fishingAnimInterval) clearInterval(fishingAnimInterval);
  if (transitTimeout) clearTimeout(transitTimeout);
  if (transitAnimInterval) clearInterval(transitAnimInterval);
});

document.addEventListener('click', () => {
  inputEl.focus();
});
